// src/pages/admin/Dashboard.js
import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const fmtCOP = v => new Intl.NumberFormat('es-CO', { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(v || 0);
const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [finanzas, setFinanzas] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [ciudades, setCiudades] = useState({ ventasCiudad:[], clientesCiudad:[] });
  const mes = MESES[new Date().getMonth()];

  useEffect(() => {
    api.get('/pedidos/stats/resumen').then(r => setStats(r.data)).catch(()=>{});
    api.get('/pedidos?limit=5').then(r => setPedidos(r.data)).catch(()=>{});
    api.get('/finanzas/resumen').then(r => setFinanzas(r.data)).catch(()=>{});
    api.get('/inventario/alertas').then(r => setAlertas(r.data)).catch(()=>{});
    api.get('/pedidos/stats/ciudades').then(r => setCiudades(r.data)).catch(()=>{});
  }, []);

  const estadoBadge = (e) => {
    const m = { nuevo:'badge-blue', preparando:'badge-yellow', en_proceso:'badge-yellow', enviado:'badge-green', entregado:'badge-green', cancelado:'badge-red' };
    const l = { nuevo:'Nuevo', preparando:'Preparando', en_proceso:'En proceso', enviado:'Enviado', entregado:'Entregado', cancelado:'Cancelado' };
    return <span className={`badge ${m[e]||'badge-gray'}`}>{l[e]||e}</span>;
  };

  return (
    <div>
      {/* Topbar */}
      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'14px 28px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:900, color:'var(--forest)' }}>Dashboard</h1>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{new Date().toLocaleDateString('es-CO', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {alertas.length > 0 && (
            <div style={{ background:'#fff8e1', border:'1px solid #f7c948', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:700, color:'#8a6000' }}>
              ⚠️ {alertas.length} producto{alertas.length>1?'s':''} con stock bajo
            </div>
          )}
          <a href="/admin/pedidos" className="btn btn-primary btn-sm">+ Nuevo pedido</a>
        </div>
      </div>

      <div style={{ padding:'24px 28px' }}>
        {/* Métricas */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          <div className="metric-card">
            <div className="metric-label">Ventas hoy</div>
            <div className="metric-value">{fmtCOP(stats?.ventas_hoy?.total)}</div>
            <div className="metric-sub metric-up">↑ {stats?.ventas_hoy?.cantidad || 0} pedidos</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Ventas {mes}</div>
            <div className="metric-value">{fmtCOP(stats?.ventas_mes?.total)}</div>
            <div className="metric-sub metric-up">{stats?.ventas_mes?.cantidad || 0} pedidos</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Pedidos activos</div>
            <div className="metric-value">{stats?.pedidos_pendientes || 0}</div>
            <div className="metric-sub" style={{ color:'var(--text-muted)' }}>por procesar</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Clientes</div>
            <div className="metric-value">{stats?.total_clientes || 0}</div>
            <div className="metric-sub metric-up">registrados</div>
          </div>
        </div>

        {/* Contenido principal */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:20 }}>
          {/* Últimos pedidos */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Últimos pedidos</span>
              <a href="/admin/pedidos" style={{ fontSize:12, color:'var(--green)', fontWeight:700 }}>Ver todos →</a>
            </div>
            <div style={{ padding:0 }}>
              {pedidos.length === 0 ? (
                <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>No hay pedidos aún</div>
              ) : (
                <table className="table">
                  <thead><tr><th>Pedido</th><th>Cliente</th><th>Total</th><th>Estado</th></tr></thead>
                  <tbody>
                    {pedidos.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontWeight:700, color:'var(--green)' }}>{p.numero}</td>
                        <td>{p.nombre_cliente}</td>
                        <td style={{ fontWeight:700 }}>{fmtCOP(p.total)}</td>
                        <td>{estadoBadge(p.estado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel derecho */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* Finanzas */}
            <div className="card">
              <div className="card-header"><span className="card-title">Finanzas {mes}</span></div>
              <div className="card-body">
                {finanzas ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                      <span style={{ color:'var(--text-muted)' }}>Ingresos</span>
                      <span style={{ fontWeight:800, color:'var(--green)' }}>{fmtCOP(finanzas.ingresos)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                      <span style={{ color:'var(--text-muted)' }}>Costos</span>
                      <span style={{ fontWeight:800, color:'var(--red)' }}>- {fmtCOP(finanzas.costos)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                      <span style={{ color:'var(--text-muted)' }}>Gastos</span>
                      <span style={{ fontWeight:800, color:'var(--red)' }}>- {fmtCOP(finanzas.gastos)}</span>
                    </div>
                    <div style={{ height:1, background:'var(--border)', margin:'4px 0' }} />
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontWeight:800 }}>Utilidad</span>
                      <span style={{ fontWeight:900, fontSize:18, color:'var(--green)' }}>{fmtCOP(finanzas.utilidad)}</span>
                    </div>
                  </div>
                ) : <div style={{ color:'var(--text-muted)', fontSize:13 }}>Cargando...</div>}
              </div>
            </div>

            {/* Alertas stock */}
            {alertas.length > 0 && (
              <div className="card" style={{ borderColor:'#f7c948' }}>
                <div className="card-header" style={{ background:'#fff8e1' }}>
                  <span className="card-title">⚠️ Stock bajo</span>
                </div>
                <div className="card-body" style={{ padding:'12px 16px' }}>
                  {alertas.map(a => (
                    <div key={a.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                      <span>{a.nombre}</span>
                      <span style={{ fontWeight:800, color:'var(--red)' }}>{a.stock} uds</span>
                    </div>
                  ))}
                  <a href="/admin/inventario" className="btn btn-outline btn-sm" style={{ marginTop:10, width:'100%', justifyContent:'center' }}>Ver inventario</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Indicadores por ciudad */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, padding:'0 28px 28px' }}>
        {/* Ventas por ciudad */}
        <div className="card">
          <div className="card-header"><span className="card-title">Ventas por ciudad (mes)</span></div>
          <div className="card-body" style={{ padding:0 }}>
            {ciudades.ventasCiudad.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin datos este mes</div>
            ) : (
              <table className="table" style={{ fontSize:13 }}>
                <thead><tr><th>Ciudad</th><th>Pedidos</th><th>Total</th></tr></thead>
                <tbody>
                  {ciudades.ventasCiudad.map(c => (
                    <tr key={c.ciudad}>
                      <td style={{ fontWeight:600 }}>{c.ciudad || 'Sin ciudad'}</td>
                      <td style={{ textAlign:'center' }}>{c.pedidos}</td>
                      <td style={{ fontWeight:700, color:'var(--green)' }}>{fmtCOP(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {/* Clientes por ciudad */}
        <div className="card">
          <div className="card-header"><span className="card-title">Clientes por ciudad</span></div>
          <div className="card-body" style={{ padding:0 }}>
            {ciudades.clientesCiudad.length === 0 ? (
              <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Sin clientes registrados</div>
            ) : (
              <table className="table" style={{ fontSize:13 }}>
                <thead><tr><th>Ciudad</th><th>Clientes</th></tr></thead>
                <tbody>
                  {ciudades.clientesCiudad.map(c => (
                    <tr key={c.ciudad}>
                      <td style={{ fontWeight:600 }}>{c.ciudad || 'Sin ciudad'}</td>
                      <td style={{ textAlign:'center', fontWeight:700 }}>{c.clientes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
